<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Create User</h1>
    </div>
    <div class="modal-card-body">
      <div class="form-grid">
        <div class="form-row">
          <label for="userName" class="form-label">Name</label>
          <TextInput v-model="draft.name" placeholder="Enter user name" ref="nameInput" />
        </div>
        <div class="form-row">
          <label for="userEmail" class="form-label">Email</label>
          <TextInput v-model="draft.email" type="email" placeholder="Enter email address" />
        </div>
        <div class="form-row">
          <label for="userPassword" class="form-label">Password</label>
          <TextInput v-model="draft.password" type="password" placeholder="Enter password (min 6 characters)" />
        </div>
        <div class="form-row">
          <label for="userRole" class="form-label">Role</label>
          <SelectInput v-model="draft.role" :options="roleOptions" placeholder="Select user role..." />
        </div>
        <div class="form-row">
          <label for="userPractices" class="form-label">Practice Access</label>
          <SelectInput v-model="draft.practiceIds" :options="practiceOptions" :multiSelect="true" placeholder="Select practices..." />
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click" @click="handleCancel">
        Cancel
      </button>
      <button class="button primary form-button" @click="handleSubmit" :disabled="isSubmitting">
        {{ isSubmitting ? 'Creating...' : 'Create User' }}
      </button>
    </div>
    <div v-if="error" class="modal-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import TextInput from '@/components/inputs/TextInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';

export default {
  name: "CreateUserModal",
  components: {
    TextInput,
    SelectInput,
    CloseIcon
  },
  emits: ['close', 'user-created'],
  data() {
    return {
      draft: {
        name: '',
        email: '',
        password: '',
        role: 'regular',
        practiceIds: []
      },
      roleOptions: [
        { value: 'regular', label: 'Regular User' },
        { value: 'admin', label: 'Admin User' }
      ],
      practices: [],
      isLoadingPractices: false,
      isSubmitting: false,
      error: null
    }
  },
  computed: {
    practiceOptions() {
      if (!this.practices || !Array.isArray(this.practices)) {
        return [];
      }
      return this.practices.map(p => ({
        value: p.practiceId,
        label: p.name
      }));
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.nameInput?.$el?.focus();
    });
    this.loadPractices();
  },
  methods: {
    async loadPractices() {
      try {
        this.isLoadingPractices = true;
        const cachedPractices = queryClient.getQueryData(['practices']);
        if (cachedPractices) {
          this.practices = cachedPractices;
        } else {
          this.practices = await queryClient.fetchQuery({
            queryKey: ['practices'],
            queryFn: () => api.fetchPractices()
          });
        }
      } catch (err) {
        console.error('Failed to fetch practices:', err);
        this.error = 'Failed to load practices';
      } finally {
        this.isLoadingPractices = false;
      }
    },
    handleCancel() {
      // Close modal via close-on-click class
    },
    async handleSubmit() {
      this.error = null;

      if (!this.draft.name || this.draft.name.trim().length === 0) {
        this.error = 'Name is required';
        return;
      }

      if (!this.draft.email || this.draft.email.trim().length === 0) {
        this.error = 'Email is required';
        return;
      }

      if (!this.draft.password || this.draft.password.length < 6) {
        this.error = 'Password must be at least 6 characters';
        return;
      }

      this.isSubmitting = true;

      try {
        const userData = {
          name: this.draft.name.trim(),
          email: this.draft.email.trim(),
          password: this.draft.password,
          isAdmin: this.draft.role === 'admin',
          practiceIds: this.draft.practiceIds
        };

        await api.createUser(userData);
        this.$emit('user-created');
      } catch (err) {
        console.error('Failed to create user:', err);
        this.error = err.message || 'Failed to create user';
        this.isSubmitting = false;
      }
    }
  }
}
</script>

<style scoped>
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
</style>
